import React, { useState } from 'react';
import NoteList from '../../../NoteList';
import { useMutation, useQuery } from '@apollo/client/react';
import { ADD_ASSET_LOAD_NOTES, EDIT_ASSET_LOAD_NOTES } from '../../../../graphql/client/mutations';
import { GET_ASSET_LOAD_NOTES } from '../../../../graphql/client/queries';
import { AddAssetLoadNotes, EditAssetLoadNotes, GetAssetLoadNotes } from '../../../../types/schemaTypes';
import { Error, Loading } from '../../../_common';
import { Note } from '../../../../interfaces/Notes/note';
import { useAuthContext } from '../../../Auth/AuthProvider';
import { PermissionEnum } from '../../../../types/PermissionEnum';
import { toast } from 'react-toastify';
import { toastOpt } from '../../../../utils/helpers/toastHelper'

interface LoadNotesProps {
    loadId: number;
}

// TODO Check the backend query for this value
// Do not change this value unless the associate backend value has been updated - (5/11/19) - KS
const PAGE_SIZE = 50;
const SuccessMessage = {
    save: 'Notes added successfully',
    update: 'Notes updated successfully'
};


const EquipmentLoadNotes = ({ loadId }: LoadNotesProps): React.ReactElement => {
    const [isEndOfResults, setIsEndOfResults] = useState(false);
    const authContext = useAuthContext();

    const canViewNotesList = authContext.userHasPermission(PermissionEnum.ViewAssetLoadNotes);
    const canAddEditNotes = authContext.userHasPermission(PermissionEnum.AddEditAssetLoadNotes);

    const onLoadNotesAddorEditFail = errorMessage => {
        const alertMessage = errorMessage[0].includes('Not permitted to update the load notes created by another user') ? 'This note can only be edited by the user who created it. You can add another note instead' : errorMessage[0];
        toast.error(alertMessage, toastOpt);
    };

    const onLoadNotesAddorEditSuccess = (action) => {
        toast.success(SuccessMessage[action], toastOpt);
    }

    const [addLoadNotes] = useMutation<AddAssetLoadNotes>(ADD_ASSET_LOAD_NOTES, {
        onError: (error) => { console.log("error", error) },
        onCompleted: (data) => {
            if (data.addAssetLoadNotes === null) return;
            const addLoadNotesError = (data.addAssetLoadNotes.errors && data.addAssetLoadNotes.errors.length > 0) ? data.addAssetLoadNotes.errors : null;
            if (addLoadNotesError) {
                onLoadNotesAddorEditFail(addLoadNotesError);
            } else {
                onLoadNotesAddorEditSuccess('save');
            }
        },
        refetchQueries: () => {
            return [
                {
                    query: GET_ASSET_LOAD_NOTES,
                    variables: { loadId: loadId },
                },
            ];
        },
    });

    const [editLoadNotes] = useMutation<EditAssetLoadNotes>(EDIT_ASSET_LOAD_NOTES, {
        onError: (error) => { console.log("error", error) },
        onCompleted: (data) => {
            if (data.editAssetLoadNotes === null) return;
            const editNoteErrors = (data.editAssetLoadNotes.errors && data.editAssetLoadNotes.errors.length > 0) ? data.editAssetLoadNotes.errors : null;
            if (editNoteErrors) {
                onLoadNotesAddorEditFail(editNoteErrors);
            } else {
                onLoadNotesAddorEditSuccess('update');
            }
        },
        refetchQueries: () => {
            return [
                {
                    query: GET_ASSET_LOAD_NOTES,
                    variables: { loadId: loadId },
                },
            ];
        },
    });

    const onSaveNote = (noteText: string): void => {
        const addLoadNoteInput = {
            notes: noteText,
            loadId: loadId,
        };
        addLoadNotes({ variables: { input: addLoadNoteInput } });
    };

    const onEditNote = (noteText: string, noteId: number): void => {
        const editLoadNoteInput = {
            id: noteId,
            newValues: {
                notes: noteText,
            },
        };
        editLoadNotes({ variables: { input: editLoadNoteInput } });
    };

    const { loading, error, data, fetchMore } = useQuery<GetAssetLoadNotes>(GET_ASSET_LOAD_NOTES, {
        variables: {
            loadId: loadId,
        },
    });

    const offset = data && data.getAssetLoadNotes ? data.getAssetLoadNotes.length / PAGE_SIZE : 0;

    React.useEffect(() => {
        if (
            offset % 1 !== 0 ||
            (!isEndOfResults && data && data.getAssetLoadNotes && data.getAssetLoadNotes.length < PAGE_SIZE)
        ) {
            // If offset is not a whole number, the end of the results has been reached. (5/11/19) - KS
            setIsEndOfResults(true);
        }
    }, [offset, data?.getAssetLoadNotes?.length]);

    if (loading) {
        return <Loading />;
    }

    if (error) {
        return <Error error={error} />;
    }

    if (data && data.getAssetLoadNotes) {
        const notesList: Note[] = [];
        if (data.getAssetLoadNotes.length > 0) {
            data.getAssetLoadNotes.forEach((item: any) => {
                const note: Note = {
                    noteId: item.id,
                    notes: item.notes,
                    createdAtUtc: item.createdAt,
                    modifiedAtUtc: item.modifiedAt,
                    createdUserId: item.createdUserId,
                    entityId: item.loadId,
                    isDeleted: item.isDeleted,
                    name: item.name,
                    userName: item.userName,
                    email: item.email,
                };
                notesList.push(note);
            });
        }

        if (!canViewNotesList) {
            return (
                <div className="NoResult-Container EquipmentNotes-Unauth-Container">
                    <div className="NoResult-First-Title-Secondary">Unauthorized</div>
                    <div className="NoResult-First-Title-Primary">You do not have permission to view this screen</div>
                </div>
            )
        }

        return (
            <NoteList
                isLoading={loading}
                isEndOfResults={isEndOfResults}
                onLoadMore={() =>
                    fetchMore({
                        variables: {
                            offset,
                        },
                        updateQuery: (prev: any, { fetchMoreResult }: any) => {
                            if (
                                prev &&
                                fetchMoreResult &&
                                prev.getAssetLoadNotes &&
                                fetchMoreResult.getAssetLoadNotes
                            ) {
                                return Object.assign({}, prev, {
                                    getAssetLoadNotes: [...prev.getAssetLoadNotes, ...fetchMoreResult.getAssetLoadNotes],
                                });
                            } else {
                                return prev;
                            }
                        },
                    })
                }
                noteItems={notesList}
                onSaveNote={onSaveNote}
                onEditNote={onEditNote}
                canAddNewNotes={canAddEditNotes}
                canViewNotesList={canViewNotesList}
                canEditNote={canAddEditNotes}
            />
        );
    } else {
        return null as any;;
    }
};

export default EquipmentLoadNotes;
